// src/components/editor/ConfigurableMenuBar.tsx
import { Editor } from "@tiptap/react";

import { Icon } from "@elaniintc/goes-design-system";
import MenuBarItem from "@/components/common/editor/menu-bar-item";

type EditorOption = {
  icon: React.ReactNode;
  onClick: () => boolean;
  pressed: boolean;
  name: string; // Identificador único
};

type AllowedOption =
  | "bold"
  | "italic"
  | "strike"
  | "underline"
  | "link"
  | "h1"
  | "h2"
  | "h3"
  | "bulletList"
  | "orderedList"
  | "blockquote";

interface ConfigurableMenuBarProps {
  editor: Editor | null;
  allowedOptions?: AllowedOption[]; // Si no se pasa, muestra todas
  isDisabled?: boolean;
}

export default function ConfigurableMenuBar({
  editor,
  allowedOptions,
  isDisabled = false,
}: ConfigurableMenuBarProps) {
  if (!editor) return null;

  const allOptions: Record<AllowedOption, EditorOption> = {
    h1: {
      name: "h1",
      icon: (
        <Icon type="far" name="h1" className="text-sm font-normal text-body" />
      ),
      onClick: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
      pressed: editor.isActive("heading", { level: 1 }),
    },
    h2: {
      name: "h2",
      icon: (
        <Icon type="far" name="h2" className="text-sm font-normal text-body" />
      ),
      onClick: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
      pressed: editor.isActive("heading", { level: 2 }),
    },
    h3: {
      name: "h3",
      icon: (
        <Icon type="far" name="h3" className="text-sm font-normal text-body" />
      ),
      onClick: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
      pressed: editor.isActive("heading", { level: 3 }),
    },
    bold: {
      name: "bold",
      icon: (
        <Icon
          type="far"
          name="bold"
          className="text-sm font-normal text-body"
        />
      ),
      onClick: () => editor.chain().focus().toggleBold().run(),
      pressed: editor.isActive("bold"),
    },
    italic: {
      name: "italic",
      icon: (
        <Icon
          type="far"
          name="italic"
          className="text-sm font-normal text-body"
        />
      ),
      onClick: () => editor.chain().focus().toggleItalic().run(),
      pressed: editor.isActive("italic"),
    },
    strike: {
      name: "strike",
      icon: (
        <Icon
          type="far"
          name="strikethrough"
          className="text-sm font-normal text-body"
        />
      ),
      onClick: () => editor.chain().focus().toggleStrike().run(),
      pressed: editor.isActive("strike"),
    },
    underline: {
      name: "underline",
      icon: (
        <Icon
          type="far"
          name="underline"
          className="text-sm font-normal text-body"
        />
      ),
      onClick: () => editor.chain().focus().toggleUnderline().run(),
      pressed: editor.isActive("underline"),
    },
    link: {
      name: "link",
      icon: (
        <Icon
          type="far"
          name="link"
          className="text-sm font-normal text-body"
        />
      ),
      onClick: () => {
        if (editor.isActive("link")) {
          return editor.chain().focus().unsetLink().run();
        }
        const url = window.prompt("URL:");
        if (!url) return false;
        return editor
          .chain()
          .focus()
          .extendMarkRange("link")
          .setLink({ href: url })
          .run();
      },
      pressed: editor.isActive("link"),
    },
    bulletList: {
      name: "bulletList",
      icon: (
        <Icon
          type="far"
          name="list"
          className="text-sm font-normal text-body"
        />
      ),
      onClick: () => editor.chain().focus().toggleBulletList().run(),
      pressed: editor.isActive("bulletList"),
    },
    orderedList: {
      name: "orderedList",
      icon: (
        <Icon
          type="far"
          name="list-ol"
          className="text-sm font-normal text-body"
        />
      ),
      onClick: () => editor.chain().focus().toggleOrderedList().run(),
      pressed: editor.isActive("orderedList"),
    },
    blockquote: {
      name: "blockquote",
      icon: (
        <Icon
          type="far"
          name="block-quote"
          className="text-sm font-normal text-body"
        />
      ),
      onClick: () => editor.chain().focus().toggleBlockquote().run(),
      pressed: editor.isActive("blockquote"),
    },
  };

  // Filtramos las opciones según lo permitido
  const enabledOptions = allowedOptions
    ? allowedOptions.map((opt) => allOptions[opt]).filter(Boolean)
    : Object.values(allOptions);

  // Agrupamos las opciones (puedes personalizar esto)
  const textFormatting = enabledOptions.filter((opt) =>
    ["bold", "italic", "strike", "underline", "link"].includes(opt.name),
  );

  const headings = enabledOptions.filter((opt) =>
    ["h1", "h2", "h3"].includes(opt.name),
  );

  const lists = enabledOptions.filter((opt) =>
    ["bulletList", "orderedList", "blockquote"].includes(opt.name),
  );

  const renderGroup = (options: EditorOption[], testId?: string) => {
    if (options.length === 0) return null;

    return (
      <div
        className="flex shadow-custom bg-neutral-object rounded-md"
        data-testid={testId}
      >
        {options.map((option, index) => (
          <MenuBarItem
            key={option.name}
            type={
              index === 0
                ? "first"
                : index === options.length - 1
                  ? "last"
                  : undefined
            }
            isPressed={option.pressed}
            onClick={option.onClick}
            icon={option.icon}
            isDisabled={isDisabled}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="flex flex-col items-center lg:flex-row gap-4">
      {renderGroup(headings, "headings")}
      {renderGroup(textFormatting, "text-formatting")}
      {renderGroup(lists, "lists")}
    </div>
  );
}
